/**
 * CommercialTab — UEIM-03
 * Commercial Costing + Client Proposal management.
 * Mounted inside OpportunityDetail as the "Commercial" tab.
 *
 * Sub-tabs: Cost Sheet | Proposals
 *
 * Truth boundaries enforced in UI:
 *   - negotiated_internal_cost / margin_percent shown in Cost Sheet ONLY
 *   - Proposals show selling_price only — cost and margin never visible
 */
import { useState, useEffect, useCallback } from "react";
import {
  DollarSign, FileText, Plus, ChevronDown, ChevronRight,
  CheckCircle2, Clock, Send, Eye, Trash2, Pencil, Download,
  AlertCircle, Loader2, BarChart3, CheckSquare,
} from "lucide-react";
import { commercialApi, sourcingApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

// ── Status helpers ─────────────────────────────────────────────────────────────
const SHEET_STATUS_STYLE = {
  DRAFT:        "bg-slate-100 text-slate-600",
  UNDER_REVIEW: "bg-amber-100 text-amber-700",
  APPROVED:     "bg-emerald-100 text-emerald-700",
  SUPERSEDED:   "bg-slate-100 text-slate-400 line-through",
};

const PROPOSAL_STATUS_STYLE = {
  DRAFT:              "bg-slate-100 text-slate-600",
  APPROVED_FOR_SEND:  "bg-indigo-100 text-indigo-700",
  SENT:               "bg-blue-100 text-blue-700",
  VIEWED:             "bg-cyan-100 text-cyan-700",
  ACCEPTED:           "bg-emerald-100 text-emerald-700",
  REJECTED:           "bg-red-100 text-red-700",
  COUNTER_PROPOSED:   "bg-amber-100 text-amber-700",
  EXPIRED:            "bg-slate-100 text-slate-500",
};

const PROPOSAL_STATUS_LABEL = {
  DRAFT: "Draft", APPROVED_FOR_SEND: "Ready to Send", SENT: "Sent",
  VIEWED: "Viewed", ACCEPTED: "Accepted", REJECTED: "Rejected",
  COUNTER_PROPOSED: "Counter-Proposed", EXPIRED: "Expired",
};

function fmt(num) {
  if (num === null || num === undefined) return "—";
  return `₹${Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ── Section separator ──────────────────────────────────────────────────────────
function SubTab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
        active
          ? "bg-white text-slate-900 shadow-sm border border-slate-200"
          : "text-slate-500 hover:text-slate-700"
      }`}
    >
      {label}
    </button>
  );
}

// ── CostLine row ───────────────────────────────────────────────────────────────
function CostLineRow({ line, sheetStatus, canManage, onRemove }) {
  return (
    <tr className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
      <td className="px-4 py-3">
        <p className="font-medium text-slate-800 text-sm">{line.description || "—"}</p>
        <p className="text-xs text-slate-400 mt-0.5">
          Qty {line.quantity} {line.unit || ""} · Supplier: {line.evidence_supplier_name}
        </p>
        {line.evidence_quoted_unit_price && (
          <p className="text-xs text-indigo-500 mt-0.5">Quoted: {fmt(line.evidence_quoted_unit_price)}/unit</p>
        )}
      </td>
      <td className="px-4 py-3 text-right text-sm font-medium text-slate-700">{fmt(line.negotiated_internal_cost)}</td>
      <td className="px-4 py-3 text-right text-sm text-slate-600">{line.gst_percent ?? 0}%</td>
      <td className="px-4 py-3 text-right text-sm text-amber-700 font-medium">{line.margin_percent ?? 0}%</td>
      <td className="px-4 py-3 text-right text-sm font-semibold text-slate-900">{fmt(line.selling_price_per_unit)}</td>
      <td className="px-4 py-3 text-right text-sm font-bold text-indigo-700">{fmt(line.selling_price_total + line.gst_amount_total)}</td>
      {canManage && sheetStatus === "DRAFT" && (
        <td className="px-4 py-3 text-center">
          <button
            onClick={() => onRemove(line.id)}
            className="text-red-400 hover:text-red-600 transition-colors"
            data-testid={`remove-cost-line-${line.id}`}
            title="Remove line"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </td>
      )}
    </tr>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function CommercialTab({ oppId }) {
  const { user } = useAuth();
  const [subTab, setSubTab] = useState("costing");
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);  // expanded sheet with lines
  const [proposals, setProposals] = useState([]);
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [loadingProposals, setLoadingProposals] = useState(false);

  // Modals
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [showCreateSheetModal, setShowCreateSheetModal] = useState(false);
  const [showCreateProposalModal, setShowCreateProposalModal] = useState(false);
  const [showCreateRevisionModal, setShowCreateRevisionModal] = useState(false);
  const [selectedProposalForRevision, setSelectedProposalForRevision] = useState(null);

  // Sourcing selections (used when adding cost lines)
  const [availableSelections, setAvailableSelections] = useState([]);
  const [loadingSelections, setLoadingSelections] = useState(false);

  const canCostingRead   = hasCapability(user, "costing.read");
  const canCostingManage = hasCapability(user, "costing.manage");
  const canCostingApprove= hasCapability(user, "costing.approve");
  const canProposalRead  = hasCapability(user, "proposal.read");
  const canProposalCreate= hasCapability(user, "proposal.create");
  const canProposalApprove = hasCapability(user, "proposal.approve");
  const canProposalSend  = hasCapability(user, "proposal.send");
  const canPdfView       = hasCapability(user, "proposal.view_client_version");

  // ── Loaders ─────────────────────────────────────────────────────────────────
  const loadSheets = useCallback(async () => {
    if (!canCostingRead) return;
    setLoadingSheet(true);
    try {
      const res = await commercialApi.listSheets(oppId);
      setSheets(res.data || []);
      // Auto-expand the first non-superseded sheet
      const first = (res.data || []).find(s => s.status !== "SUPERSEDED");
      if (first) {
        const detail = await commercialApi.getSheet(first.id);
        setActiveSheet(detail.data);
      } else {
        setActiveSheet(null);
      }
    } catch { toast.error("Failed to load cost sheets"); }
    finally { setLoadingSheet(false); }
  }, [oppId, canCostingRead]);

  const loadProposals = useCallback(async () => {
    if (!canProposalRead) return;
    setLoadingProposals(true);
    try {
      const res = await commercialApi.listProposals(oppId);
      setProposals(res.data || []);
    } catch { toast.error("Failed to load proposals"); }
    finally { setLoadingProposals(false); }
  }, [oppId, canProposalRead]);

  useEffect(() => { loadSheets(); }, [loadSheets]);
  useEffect(() => { if (subTab === "proposals") loadProposals(); }, [subTab, loadProposals]);

  // ── Cost sheet actions ────────────────────────────────────────────────────────
  const handleCreateSheet = async (title) => {
    try {
      await commercialApi.createSheet({ opportunity_id: oppId, title });
      setShowCreateSheetModal(false);
      toast.success("Cost sheet created");
      await loadSheets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create sheet");
    }
  };

  const handleSubmitReview = async () => {
    if (!activeSheet) return;
    try {
      await commercialApi.submitSheetReview(activeSheet.id);
      toast.success("Submitted for review");
      await loadSheets();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleApproveSheet = async () => {
    if (!activeSheet) return;
    try {
      await commercialApi.approveSheet(activeSheet.id, {});
      toast.success("Cost sheet approved");
      await loadSheets();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to approve"); }
  };

  const handleSupersede = async () => {
    if (!activeSheet) return;
    const reason = window.prompt("Reason for superseding this cost sheet?");
    if (!reason) return;
    try {
      await commercialApi.supersede(activeSheet.id, { reason });
      toast.success("Sheet superseded. New DRAFT created.");
      await loadSheets();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleRemoveLine = async (lid) => {
    if (!activeSheet) return;
    try {
      await commercialApi.removeCostLine(activeSheet.id, lid);
      toast.success("Line removed");
      const detail = await commercialApi.getSheet(activeSheet.id);
      setActiveSheet(detail.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // ── Add cost line: load available sourcing selections ────────────────────────
  const handleOpenAddLine = async () => {
    setLoadingSelections(true);
    setShowAddLineModal(true);
    try {
      // Gather all packages for this opportunity
      const pkgRes = await sourcingApi.listPackages({ opportunity_id: oppId });
      const packages = pkgRes.data?.packages || pkgRes.data?.results || (Array.isArray(pkgRes.data) ? pkgRes.data : []);
      const allSels = [];
      for (const pkg of packages) {
        try {
          const selRes = await sourcingApi.listSelections(pkg.id);
          (selRes.data || []).forEach(sel => {
            // Exclude if already added to this sheet
            const alreadyAdded = activeSheet?.lines?.some(l => l.supplier_selection_id === sel.id);
            if (!alreadyAdded) allSels.push({ ...sel, package_title: pkg.title });
          });
        } catch { /* skip failed package */ }
      }
      setAvailableSelections(allSels);
    } catch { toast.error("Failed to load supplier selections"); }
    finally { setLoadingSelections(false); }
  };

  // ── Proposal actions ──────────────────────────────────────────────────────────
  const handleApproveRevision = async (pid, rid) => {
    try {
      await commercialApi.approveRevision(pid, rid, {});
      toast.success("Revision approved for send");
      await loadProposals();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleSendRevision = async (pid, rid) => {
    try {
      await commercialApi.sendRevision(pid, rid, {});
      toast.success("Revision marked as sent");
      await loadProposals();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleMarkViewed = async (pid, rid) => {
    try {
      await commercialApi.markViewed(pid, rid);
      toast.success("Marked as viewed by client");
      await loadProposals();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleRespond = async (pid, rid, response) => {
    try {
      await commercialApi.respond(pid, rid, { response });
      toast.success(`Recorded: ${response}`);
      await loadProposals();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  if (!canCostingRead && !canProposalRead) {
    return (
      <div className="py-16 text-center text-slate-400">
        <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <p>You do not have access to commercial data for this opportunity.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sub-tab switcher */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        <SubTab label="Cost Sheet" active={subTab === "costing"} onClick={() => setSubTab("costing")} />
        <SubTab label="Proposals" active={subTab === "proposals"} onClick={() => setSubTab("proposals")} />
      </div>

      {/* ── COSTING sub-tab ─────────────────────────────────────────────────── */}
      {subTab === "costing" && canCostingRead && (
        <div className="space-y-4">
          {loadingSheet ? (
            <div className="py-12 text-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading cost sheet…
            </div>
          ) : !activeSheet ? (
            <div className="py-16 text-center bg-white rounded-xl border border-slate-200" data-testid="no-cost-sheet">
              <DollarSign className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 font-medium">No cost sheet yet</p>
              <p className="text-slate-400 text-sm mt-1 mb-4">Create a cost sheet to start building internal pricing for this opportunity.</p>
              {canCostingManage && (
                <Button onClick={() => setShowCreateSheetModal(true)} data-testid="create-sheet-btn">
                  <Plus className="w-4 h-4 mr-1" /> Create Cost Sheet
                </Button>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {/* Sheet header */}
              <div className="bg-slate-50 px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">{activeSheet.title}</h3>
                    <Badge className={`text-xs border-0 ${SHEET_STATUS_STYLE[activeSheet.status] || "bg-slate-100 text-slate-600"}`}>
                      {activeSheet.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">v{activeSheet.version_number} · {activeSheet.lines?.length || 0} line(s)</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {canCostingManage && activeSheet.status === "DRAFT" && (
                    <>
                      <Button size="sm" variant="outline" onClick={handleOpenAddLine} data-testid="add-cost-line-btn">
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add Line
                      </Button>
                      <Button size="sm" onClick={handleSubmitReview} data-testid="submit-review-btn">
                        Submit for Review
                      </Button>
                    </>
                  )}
                  {canCostingApprove && activeSheet.status === "UNDER_REVIEW" && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleApproveSheet} data-testid="approve-sheet-btn">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve Sheet
                    </Button>
                  )}
                  {canCostingManage && activeSheet.status === "APPROVED" && (
                    <Button size="sm" variant="outline" onClick={handleSupersede} data-testid="supersede-sheet-btn">
                      Supersede & Revise
                    </Button>
                  )}
                </div>
              </div>

              {/* Cost lines table */}
              {!activeSheet.lines || activeSheet.lines.length === 0 ? (
                <div className="py-10 text-center text-slate-400 text-sm" data-testid="no-cost-lines">
                  No lines added yet. Add a line from a confirmed supplier selection.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-max text-sm">
                    <thead className="bg-slate-50 border-b border-slate-100 text-xs">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium text-slate-600 whitespace-nowrap">Item</th>
                        <th className="text-right px-4 py-2.5 font-medium text-slate-600 whitespace-nowrap">Unit Cost</th>
                        <th className="text-right px-4 py-2.5 font-medium text-slate-600 whitespace-nowrap">GST</th>
                        <th className="text-right px-4 py-2.5 font-medium text-amber-700 whitespace-nowrap">Margin</th>
                        <th className="text-right px-4 py-2.5 font-medium text-slate-600 whitespace-nowrap">Unit Sell</th>
                        <th className="text-right px-4 py-2.5 font-medium text-indigo-700 whitespace-nowrap">Total (incl GST)</th>
                        {canCostingManage && activeSheet.status === "DRAFT" && <th className="px-4 py-2.5"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {activeSheet.lines.map(line => (
                        <CostLineRow
                          key={line.id}
                          line={line}
                          sheetStatus={activeSheet.status}
                          canManage={canCostingManage}
                          onRemove={handleRemoveLine}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Totals */}
              {activeSheet.lines?.length > 0 && (
                <div className="border-t border-slate-100 bg-slate-50 px-5 py-3 flex justify-end gap-8 text-sm">
                  <div className="text-right">
                    <p className="text-xs text-slate-400">Internal Cost Total</p>
                    <p className="font-semibold text-slate-700">{fmt(activeSheet.total_negotiated_cost)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400">Selling Total + GST</p>
                    <p className="font-bold text-indigo-700 text-base">{fmt(activeSheet.grand_total_selling)}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── PROPOSALS sub-tab ─────────────────────────────────────────────── */}
      {subTab === "proposals" && canProposalRead && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">{proposals.length} proposal{proposals.length !== 1 ? "s" : ""}</p>
            {canProposalCreate && (
              <Button size="sm" onClick={() => setShowCreateProposalModal(true)} data-testid="create-proposal-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> New Proposal
              </Button>
            )}
          </div>

          {loadingProposals ? (
            <div className="py-12 text-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Loading…
            </div>
          ) : proposals.length === 0 ? (
            <div className="py-16 text-center bg-white rounded-xl border border-slate-200" data-testid="no-proposals">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 font-medium">No proposals yet</p>
              <p className="text-slate-400 text-sm mt-1 mb-4">Create a proposal from an approved cost sheet.</p>
              {canProposalCreate && (
                <Button onClick={() => setShowCreateProposalModal(true)}>
                  <Plus className="w-4 h-4 mr-1" /> Create Proposal
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {proposals.map(prop => (
                <ProposalCard
                  key={prop.id}
                  proposal={prop}
                  canCreate={canProposalCreate}
                  canApprove={canProposalApprove}
                  canSend={canProposalSend}
                  canPdf={canPdfView}
                  onApprove={handleApproveRevision}
                  onSend={handleSendRevision}
                  onMarkViewed={handleMarkViewed}
                  onRespond={handleRespond}
                  onCreateRevision={() => {
                    setSelectedProposalForRevision(prop);
                    setShowCreateRevisionModal(true);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {showCreateSheetModal && (
        <CreateSheetModal
          onClose={() => setShowCreateSheetModal(false)}
          onCreate={handleCreateSheet}
        />
      )}

      {showAddLineModal && (
        <AddCostLineModal
          sheetId={activeSheet?.id}
          selections={availableSelections}
          loading={loadingSelections}
          onClose={() => setShowAddLineModal(false)}
          onAdded={async () => {
            setShowAddLineModal(false);
            const detail = await commercialApi.getSheet(activeSheet.id);
            setActiveSheet(detail.data);
          }}
        />
      )}

      {showCreateProposalModal && (
        <CreateProposalModal
          oppId={oppId}
          onClose={() => setShowCreateProposalModal(false)}
          onCreate={async () => {
            setShowCreateProposalModal(false);
            await loadProposals();
          }}
        />
      )}

      {showCreateRevisionModal && selectedProposalForRevision && (
        <CreateRevisionModal
          proposal={selectedProposalForRevision}
          oppId={oppId}
          onClose={() => { setShowCreateRevisionModal(false); setSelectedProposalForRevision(null); }}
          onCreated={async () => {
            setShowCreateRevisionModal(false);
            setSelectedProposalForRevision(null);
            await loadProposals();
          }}
        />
      )}
    </div>
  );
}

// ── ProposalCard (with inline revision list) ─────────────────────────────────
function ProposalCard({ proposal, canCreate, canApprove, canSend, canPdf, onApprove, onSend, onMarkViewed, onRespond, onCreateRevision }) {
  const [expanded, setExpanded] = useState(true);
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadRevisions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await commercialApi.listRevisions(proposal.id);
      setRevisions(res.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [proposal.id]);

  useEffect(() => { if (expanded) loadRevisions(); }, [expanded, loadRevisions]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`proposal-card-${proposal.id}`}>
      <div
        className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <div>
            <p className="font-semibold text-slate-900">{proposal.title}</p>
            <p className="text-xs text-slate-400 mt-0.5">Rev {proposal.current_revision_number}</p>
          </div>
        </div>
        {canCreate && (
          <Button
            size="sm" variant="outline"
            onClick={e => { e.stopPropagation(); onCreateRevision(); }}
            data-testid={`new-revision-btn-${proposal.id}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> New Revision
          </Button>
        )}
      </div>

      {expanded && (
        <div className="border-t border-slate-100">
          {loading ? (
            <div className="py-6 text-center text-slate-400 text-sm">Loading revisions…</div>
          ) : revisions.length === 0 ? (
            <div className="py-6 text-center text-slate-400 text-sm" data-testid="no-revisions">
              No revisions yet. Create the first revision from an approved cost sheet.
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {revisions.map(rev => (
                <RevisionRow
                  key={rev.id}
                  rev={rev}
                  proposalId={proposal.id}
                  canApprove={canApprove}
                  canSend={canSend}
                  canPdf={canPdf}
                  onApprove={() => onApprove(proposal.id, rev.id)}
                  onSend={() => onSend(proposal.id, rev.id)}
                  onMarkViewed={() => onMarkViewed(proposal.id, rev.id)}
                  onRespond={(r) => onRespond(proposal.id, rev.id, r)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── RevisionRow ────────────────────────────────────────────────────────────────
function RevisionRow({ rev, proposalId, canApprove, canSend, canPdf, onApprove, onSend, onMarkViewed, onRespond }) {
  const [showDetail, setShowDetail] = useState(false);
  const [detail, setDetail] = useState(null);

  const loadDetail = async () => {
    if (detail) { setShowDetail(s => !s); return; }
    try {
      const res = await commercialApi.getRevision(proposalId, rev.id);
      setDetail(res.data);
      setShowDetail(true);
    } catch { toast.error("Failed to load revision detail"); }
  };

  const pdfUrl = commercialApi.pdfUrl(proposalId, rev.id);

  return (
    <div data-testid={`revision-row-${rev.id}`}>
      <div className="px-5 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <button onClick={loadDetail} className="text-slate-400 hover:text-slate-600">
            {showDetail ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-slate-800 text-sm">Rev {rev.revision_number} — {rev.subject}</p>
              <Badge className={`text-xs border-0 ${PROPOSAL_STATUS_STYLE[rev.status] || "bg-slate-100 text-slate-600"}`}>
                {PROPOSAL_STATUS_LABEL[rev.status] || rev.status}
              </Badge>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              Total: <span className="font-medium text-slate-700">{fmt(rev.grand_total)}</span>
              {rev.valid_until && ` · Valid until ${rev.valid_until}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canApprove && rev.status === "DRAFT" && (
            <Button size="sm" variant="outline" onClick={onApprove} data-testid={`approve-revision-${rev.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
            </Button>
          )}
          {canSend && rev.status === "APPROVED_FOR_SEND" && (
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={onSend} data-testid={`send-revision-${rev.id}`}>
              <Send className="w-3.5 h-3.5 mr-1" /> Send
            </Button>
          )}
          {rev.status === "SENT" && (
            <Button size="sm" variant="outline" onClick={onMarkViewed} data-testid={`mark-viewed-${rev.id}`}>
              <Eye className="w-3.5 h-3.5 mr-1" /> Mark Viewed
            </Button>
          )}
          {(rev.status === "SENT" || rev.status === "VIEWED") && (
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => onRespond("ACCEPTED")} data-testid={`accept-revision-${rev.id}`}>
                Accept
              </Button>
              <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50" onClick={() => onRespond("REJECTED")} data-testid={`reject-revision-${rev.id}`}>
                Reject
              </Button>
              <Button size="sm" variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => onRespond("COUNTER_PROPOSED")} data-testid={`counter-revision-${rev.id}`}>
                Counter
              </Button>
            </div>
          )}
          {canPdf && (
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" download>
              <Button size="sm" variant="ghost" className="text-slate-500" data-testid={`pdf-download-${rev.id}`}>
                <Download className="w-3.5 h-3.5 mr-1" /> PDF
              </Button>
            </a>
          )}
        </div>
      </div>

      {showDetail && detail && (
        <div className="mx-5 mb-4 bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-100 border-b border-slate-200">
                <th className="text-left px-3 py-2 font-medium text-slate-500">Description</th>
                <th className="text-right px-3 py-2 font-medium text-slate-500">Qty</th>
                <th className="text-right px-3 py-2 font-medium text-slate-500">Unit Price</th>
                <th className="text-center px-3 py-2 font-medium text-slate-500">GST</th>
                <th className="text-right px-3 py-2 font-medium text-slate-500">Total (incl GST)</th>
              </tr>
            </thead>
            <tbody>
              {detail.line_items?.map(li => (
                <tr key={li.id} className="border-b border-slate-100">
                  <td className="px-3 py-2 text-slate-700">{li.description}</td>
                  <td className="px-3 py-2 text-right">{li.quantity} {li.unit || ""}</td>
                  <td className="px-3 py-2 text-right">{fmt(li.unit_selling_price)}</td>
                  <td className="px-3 py-2 text-center">{li.gst_percent ?? 0}%</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmt(li.total_with_gst)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {detail.payment_schedule?.length > 0 && (
            <div className="px-3 py-2 border-t border-slate-200">
              <p className="text-xs font-medium text-slate-500 mb-1">Payment Schedule</p>
              {detail.payment_schedule.map(ps => (
                <div key={ps.id} className="flex justify-between text-xs text-slate-600 py-0.5">
                  <span>{ps.milestone_label}</span>
                  <span>{ps.due_date || "—"} — <strong>{fmt(ps.amount)}</strong></span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Modal: Create Cost Sheet ───────────────────────────────────────────────────
function CreateSheetModal({ onClose, onCreate }) {
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handle = async () => {
    setSubmitting(true);
    try { await onCreate(title || undefined); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" data-testid="create-sheet-modal">
        <h3 className="font-semibold text-slate-900 text-lg mb-4">Create Cost Sheet</h3>
        <Label className="text-sm text-slate-600">Title (optional)</Label>
        <Input
          className="mt-1 mb-4"
          placeholder="e.g. Cost Sheet v1 — Dec 2026 Conference"
          value={title}
          onChange={e => setTitle(e.target.value)}
          data-testid="sheet-title-input"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handle} disabled={submitting} data-testid="create-sheet-submit">
            {submitting ? "Creating…" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Modal: Add Cost Line ──────────────────────────────────────────────────────
function AddCostLineModal({ sheetId, selections, loading, onClose, onAdded }) {
  const [selectedSel, setSelectedSel] = useState(null);
  const [form, setForm] = useState({ negotiated_internal_cost: "", gst_percent: "18", margin_percent: "20", internal_notes: "", delivery_notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const handle = async () => {
    if (!selectedSel) { toast.error("Select a supplier selection"); return; }
    if (!form.negotiated_internal_cost) { toast.error("Enter the negotiated internal cost"); return; }
    setSubmitting(true);
    try {
      await commercialApi.addCostLine(sheetId, {
        supplier_selection_id: selectedSel.id,
        negotiated_internal_cost: parseFloat(form.negotiated_internal_cost),
        gst_percent: parseFloat(form.gst_percent) || 0,
        margin_percent: parseFloat(form.margin_percent) || 0,
        internal_notes: form.internal_notes || undefined,
        delivery_notes: form.delivery_notes || undefined,
      });
      toast.success("Cost line added");
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add line");
    } finally { setSubmitting(false); }
  };

  // Compute preview
  const cost = parseFloat(form.negotiated_internal_cost) || 0;
  const gst = parseFloat(form.gst_percent) || 0;
  const margin = parseFloat(form.margin_percent) || 0;
  const qty = selectedSel?.requirement_quantity || 1;
  const sellUnit = cost * (1 + margin / 100);
  const gstOnSell = sellUnit * gst / 100;
  const total = (sellUnit + gstOnSell) * qty;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 overflow-y-auto max-h-[90vh]" data-testid="add-cost-line-modal">
        <h3 className="font-semibold text-slate-900 text-lg mb-4">Add Cost Line from Supplier Selection</h3>

        {loading ? (
          <div className="py-8 text-center text-slate-400">Loading selections…</div>
        ) : selections.length === 0 ? (
          <div className="py-8 text-center text-slate-400">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No confirmed supplier selections available.</p>
            <p className="text-sm mt-1">Complete sourcing and make selections first.</p>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <Label className="text-sm text-slate-600 mb-2 block">Select Supplier Selection</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2">
                {selections.map(sel => (
                  <button
                    key={sel.id}
                    onClick={() => setSelectedSel(sel)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${selectedSel?.id === sel.id ? "bg-indigo-50 border border-indigo-200" : "hover:bg-slate-50 border border-transparent"}`}
                    data-testid={`sel-option-${sel.id}`}
                  >
                    <span className="font-medium text-slate-800">{sel.supplier_name}</span>
                    <span className="text-slate-500 ml-2">— {sel.requirement_description || "Item"}</span>
                    {sel.evidence_quoted_unit_price && (
                      <span className="text-indigo-500 text-xs ml-2">({fmt(sel.evidence_quoted_unit_price)}/unit quoted)</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Negotiated Cost/Unit (₹) <span className="text-red-400">*</span></Label>
                <Input
                  type="number" min="0" step="100"
                  value={form.negotiated_internal_cost}
                  onChange={e => setForm(f => ({...f, negotiated_internal_cost: e.target.value}))}
                  placeholder="e.g. 40000"
                  data-testid="cost-input"
                />
                <p className="text-xs text-slate-400 mt-0.5">Internal / negotiated — not shown to client</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">GST %</Label>
                <Input type="number" min="0" max="100" step="0.5" value={form.gst_percent} onChange={e => setForm(f => ({...f, gst_percent: e.target.value}))} />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Margin %</Label>
                <Input type="number" min="0" max="100" step="1" value={form.margin_percent} onChange={e => setForm(f => ({...f, margin_percent: e.target.value}))} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Internal Notes</Label>
                <Input value={form.internal_notes} onChange={e => setForm(f => ({...f, internal_notes: e.target.value}))} placeholder="Internal only" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Delivery Notes (client-facing)</Label>
                <Input value={form.delivery_notes} onChange={e => setForm(f => ({...f, delivery_notes: e.target.value}))} placeholder="e.g. Setup by 8am on event day" />
              </div>
            </div>

            {cost > 0 && (
              <div className="bg-indigo-50 rounded-lg px-4 py-3 text-sm mb-4 border border-indigo-100" data-testid="cost-preview">
                <p className="font-medium text-indigo-800 mb-1">Preview</p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><span className="text-slate-500">Selling price/unit:</span><br/><strong>{fmt(sellUnit)}</strong></div>
                  <div><span className="text-slate-500">GST/unit:</span><br/><strong>{fmt(gstOnSell)}</strong></div>
                  <div><span className="text-slate-500">Total (qty {qty}):</span><br/><strong className="text-indigo-700">{fmt(total)}</strong></div>
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {selections.length > 0 && (
            <Button onClick={handle} disabled={submitting || !selectedSel} data-testid="add-line-submit">
              {submitting ? "Adding…" : "Add Line"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Modal: Create Proposal ────────────────────────────────────────────────────
function CreateProposalModal({ oppId, onClose, onCreate }) {
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handle = async () => {
    if (!title.trim()) { toast.error("Enter a proposal title"); return; }
    setSubmitting(true);
    try {
      await commercialApi.createProposal({ opportunity_id: oppId, title });
      toast.success("Proposal created");
      onCreate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" data-testid="create-proposal-modal">
        <h3 className="font-semibold text-slate-900 text-lg mb-4">New Proposal</h3>
        <Label className="text-sm text-slate-600">Proposal Title</Label>
        <Input
          className="mt-1 mb-4"
          placeholder="e.g. Dec 2026 — Corporate Conference Proposal"
          value={title}
          onChange={e => setTitle(e.target.value)}
          data-testid="proposal-title-input"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handle} disabled={submitting} data-testid="create-proposal-submit">
            {submitting ? "Creating…" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Modal: Create Proposal Revision ──────────────────────────────────────────
function CreateRevisionModal({ proposal, oppId, onClose, onCreated }) {
  const [approvedSheets, setApprovedSheets] = useState([]);
  const [form, setForm] = useState({
    cost_sheet_id: "",
    subject: "",
    cover_message: "",
    valid_until: "",
    terms_and_conditions: "",
  });
  const [lineItems, setLineItems] = useState([]);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [costSheetDetail, setCostSheetDetail] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchSheets = async () => {
      try {
        const res = await commercialApi.listSheets(oppId);
        const approved = (res.data || []).filter(s => s.status === "APPROVED");
        setApprovedSheets(approved);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetchSheets();
  }, [oppId]);

  const handleSheetSelect = async (csid) => {
    setForm(f => ({ ...f, cost_sheet_id: csid }));
    if (!csid) { setCostSheetDetail(null); setLineItems([]); return; }
    try {
      const res = await commercialApi.getSheet(csid);
      setCostSheetDetail(res.data);
      // Pre-populate line items from cost sheet lines
      setLineItems((res.data.lines || []).map(l => ({
        cost_line_id: l.id,
        description: l.description || "",
        quantity: l.quantity,
        unit: l.unit || "",
        unit_selling_price: l.selling_price_per_unit,
        gst_percent: l.gst_percent,
        delivery_notes: l.delivery_notes || "",
      })));
    } catch { toast.error("Failed to load sheet detail"); }
  };

  const addScheduleItem = () => setScheduleItems(s => [...s, { milestone_label: "", amount: "", due_date: "" }]);
  const removeScheduleItem = (i) => setScheduleItems(s => s.filter((_, idx) => idx !== i));
  const updateScheduleItem = (i, field, val) => setScheduleItems(s => s.map((item, idx) => idx === i ? { ...item, [field]: val } : item));

  const updateLineItem = (i, field, val) => setLineItems(l => l.map((item, idx) => idx === i ? { ...item, [field]: val } : item));

  const handle = async () => {
    if (!form.cost_sheet_id) { toast.error("Select an approved cost sheet"); return; }
    if (!form.subject.trim()) { toast.error("Enter a subject"); return; }
    if (lineItems.length === 0) { toast.error("No line items to include"); return; }
    setSubmitting(true);
    try {
      const payload = {
        cost_sheet_id: form.cost_sheet_id,
        subject: form.subject,
        cover_message: form.cover_message || undefined,
        valid_until: form.valid_until || undefined,
        terms_and_conditions: form.terms_and_conditions || undefined,
        line_items: lineItems.map(li => ({
          cost_line_id: li.cost_line_id,
          description: li.description,
          quantity: parseFloat(li.quantity),
          unit: li.unit || undefined,
          unit_selling_price: parseFloat(li.unit_selling_price),
          gst_percent: parseFloat(li.gst_percent) || 0,
          delivery_notes: li.delivery_notes || undefined,
        })),
        payment_schedule: scheduleItems.length > 0 ? scheduleItems.map(ps => ({
          milestone_label: ps.milestone_label,
          amount: parseFloat(ps.amount),
          due_date: ps.due_date || undefined,
        })) : undefined,
      };
      await commercialApi.createRevision(proposal.id, payload);
      toast.success("Revision created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create revision");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 overflow-y-auto max-h-[92vh]" data-testid="create-revision-modal">
        <h3 className="font-semibold text-slate-900 text-lg mb-1">New Proposal Revision</h3>
        <p className="text-sm text-slate-500 mb-4">for: {proposal.title}</p>

        {loading ? (
          <div className="py-8 text-center text-slate-400">Loading…</div>
        ) : approvedSheets.length === 0 ? (
          <div className="py-8 text-center text-slate-400">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No approved cost sheets found.</p>
            <p className="text-sm mt-1">Approve a cost sheet first to create a proposal revision.</p>
          </div>
        ) : (
          <>
            {/* Source cost sheet */}
            <div className="mb-4">
              <Label className="text-sm font-medium text-slate-700 mb-1 block">Source Cost Sheet (APPROVED) <span className="text-red-400">*</span></Label>
              <select
                value={form.cost_sheet_id}
                onChange={e => handleSheetSelect(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                data-testid="sheet-select"
              >
                <option value="">— Select cost sheet —</option>
                {approvedSheets.map(s => (
                  <option key={s.id} value={s.id}>{s.title} (v{s.version_number})</option>
                ))}
              </select>
            </div>

            {/* Subject and cover */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Subject <span className="text-red-400">*</span></Label>
                <Input value={form.subject} onChange={e => setForm(f => ({...f, subject: e.target.value}))} placeholder="e.g. Proposal for Dec 2026 Conference" data-testid="revision-subject" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Valid Until</Label>
                <Input type="date" value={form.valid_until} onChange={e => setForm(f => ({...f, valid_until: e.target.value}))} />
              </div>
            </div>
            <div className="mb-4">
              <Label className="text-xs text-slate-500 mb-1 block">Cover Message</Label>
              <Textarea value={form.cover_message} onChange={e => setForm(f => ({...f, cover_message: e.target.value}))} placeholder="Dear [Client Name], we are pleased to present our proposal…" rows={3} />
            </div>

            {/* Line items (derived from cost sheet, editable for client-facing description/price) */}
            {lineItems.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-slate-600 mb-2">Line Items (client-facing — edit descriptions and prices as needed)</p>
                <div className="space-y-2">
                  {lineItems.map((li, i) => (
                    <div key={i} className="grid grid-cols-5 gap-2 bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-400">Description</Label>
                        <Input size="sm" value={li.description} onChange={e => updateLineItem(i, "description", e.target.value)} className="mt-0.5 text-sm h-8" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400">Qty</Label>
                        <Input size="sm" type="number" value={li.quantity} onChange={e => updateLineItem(i, "quantity", e.target.value)} className="mt-0.5 text-sm h-8" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400">Unit Price (₹)</Label>
                        <Input size="sm" type="number" value={li.unit_selling_price} onChange={e => updateLineItem(i, "unit_selling_price", e.target.value)} className="mt-0.5 text-sm h-8" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400">GST %</Label>
                        <Input size="sm" type="number" value={li.gst_percent} onChange={e => updateLineItem(i, "gst_percent", e.target.value)} className="mt-0.5 text-sm h-8" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment schedule */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-slate-600">Payment Schedule (optional)</p>
                <button onClick={addScheduleItem} className="text-xs text-indigo-600 hover:underline">+ Add milestone</button>
              </div>
              {scheduleItems.map((ps, i) => (
                <div key={i} className="grid grid-cols-4 gap-2 mb-2">
                  <div className="col-span-2">
                    <Input placeholder="Milestone label" value={ps.milestone_label} onChange={e => updateScheduleItem(i, "milestone_label", e.target.value)} className="text-sm h-8" />
                  </div>
                  <Input placeholder="Amount" type="number" value={ps.amount} onChange={e => updateScheduleItem(i, "amount", e.target.value)} className="text-sm h-8" />
                  <div className="flex gap-1">
                    <Input type="date" value={ps.due_date} onChange={e => updateScheduleItem(i, "due_date", e.target.value)} className="text-sm h-8" />
                    <button onClick={() => removeScheduleItem(i)} className="text-red-400 hover:text-red-600 px-1">×</button>
                  </div>
                </div>
              ))}
            </div>

            {/* T&C */}
            <div className="mb-4">
              <Label className="text-xs text-slate-500 mb-1 block">Terms &amp; Conditions</Label>
              <Textarea value={form.terms_and_conditions} onChange={e => setForm(f => ({...f, terms_and_conditions: e.target.value}))} placeholder="1. Payment due within 30 days of invoice…" rows={3} />
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {approvedSheets.length > 0 && (
            <Button onClick={handle} disabled={submitting || !form.cost_sheet_id} data-testid="create-revision-submit">
              {submitting ? "Creating…" : "Create Revision"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
